import styled from 'styled-components';

// Shared styles for ChatInput components (Scene and DS)

interface BaseProps {
	isMobile?: boolean;
}

export const Base = styled.div<BaseProps>`
	// layout
	position: relative;
	bottom: 0;
	left: 0;
	right: 0;
	padding: ${({ isMobile }) => (isMobile ? '16px' : '12px')};
	display: flex;
	flex-direction: column;

	// style
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
	gap: ${({ isMobile }) => (isMobile ? '12px' : '8px')};
	background-color: ${({ theme }) => theme.gray[0]};
`;

interface PromptProps {
	isFocused: boolean;
}

export const Prompt = styled.div<PromptProps>`
	// layout
	width: 100%;

	// style
	background-color: ${({ theme }) => theme.gray[100]};
	border-radius: 4px;
	border: ${({ isFocused, theme }) =>
		isFocused ? `1px solid ${theme.gray[400]}` : '1px solid transparent'};
`;

interface MultilineInputWrapperProps {
	isMobile?: boolean;
}

export const MultilineInputWrapper = styled.div<MultilineInputWrapperProps>`
	max-height: ${({ isMobile }) => (isMobile ? '150px' : '35vh')};
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};
	border-radius: 4px;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: ${({ theme }) => theme.gray[100]};
	}

	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.gray[400]};
		border-radius: 3px;
	}
`;

interface ActionsProps {
	isMobile?: boolean;
}

export const Actions = styled.div<ActionsProps>`
	padding: ${({ isMobile }) => (isMobile ? '12px' : '4px')};
	padding-left: ${({ isMobile }) => (isMobile ? '12px' : '4px')};
	padding-right: ${({ isMobile }) => (isMobile ? '12px' : '4px')};
	display: flex;
	justify-content: ${({ isMobile }) => (isMobile ? 'flex-start' : 'flex-end')};
	align-items: center;
	gap: ${({ isMobile }) => (isMobile ? '10px' : '4px')};
`;

export const GenerationOptions = styled.div`
	// layout
	display: flex;
	gap: 8px;
	align-items: center;
`;

export const Spacer = styled.div`
	flex: 1;
`;

export const LilWidthSpacer = styled.div`
	width: 2px;
	flex-shrink: 0;
`;

interface WrapperProps {
	isMobile?: boolean;
}

export const SubmitButtonWrapper = styled.div<WrapperProps>`
	padding-top: ${({ isMobile }) => (isMobile ? '6px' : '4px')};
	padding-bottom: ${({ isMobile }) => (isMobile ? '6px' : '0')};
	padding-right: ${({ isMobile }) => (isMobile ? '2px' : '0')};
	transform: ${({ isMobile }) => (isMobile ? 'scale(1.5)' : 'scale(1)')};
	transform-origin: center;
	flex-shrink: 0;
`;

export const LoadingWrapper = styled.div<WrapperProps>`
	padding-top: ${({ isMobile }) => (isMobile ? '6px' : '4px')};
	flex-shrink: 0;
`;

interface MobileToastProps {
	isVisible: boolean;
}

export const MobileToast = styled.div<MobileToastProps>`
	position: fixed;
	top: calc(env(safe-area-inset-top, 0px) + 12px);
	left: 50%;
	transform: translateX(-50%);
	max-width: 90vw;
	background-color: ${({ theme }) => theme.gray[1000]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 12px 18px;
	border-radius: 999px;
	box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
	opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
	transition: opacity 0.2s ease;
	font-family: Inter;
	font-size: 16px;
	font-weight: 500;
	line-height: 1.4;
	text-align: center;
	pointer-events: none;
	z-index: 999;
`;

export const ErrorIconOverlay = styled.div`
	position: absolute;
	top: 0px;
	right: 0px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
`;

export const DsPicks = styled.div`
	padding: 8px 6px 0 6px;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
`;

export const ClearAllButton = styled.button`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[400]};
	background: transparent;
	border: none;
	padding: 2px 4px;
	border-radius: 4px;
	transition: color 0.15s ease;

	&:hover {
		color: ${({ theme }) => theme.gray[700]};
	}
`;

export const HistoricalRibbon = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 4px 8px;
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const RibbonLink = styled.button`
	color: ${({ theme }) => theme.gray[700]};
	background: none;
	border: none;
	padding: 0;
	font: inherit;
	transition: color 0.15s ease;
	&:hover {
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const LegacyOverlay = styled.div<BaseProps>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 2;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: start;
	gap: 10px;
	background-color: ${({ theme }) => theme.gray[0]}CC;
	border-radius: 4px;
	padding: 24px;
`;

export const LegacyMessage = styled.div<BaseProps>`
	font-size: ${({ isMobile }) => (isMobile ? '15px' : '13px')};
	line-height: 1.5;
	color: ${({ theme }) => theme.gray[600]};
	text-align: center;
`;

export const LegacyNewChatButton = styled.button<BaseProps>`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: ${({ isMobile }) => (isMobile ? '8px 16px' : '6px 12px')};
	font-size: ${({ isMobile }) => (isMobile ? '15px' : '13px')};
	font-weight: 500;
	color: ${({ theme }) => theme.gray[0]};
	background-color: ${({ theme }) => theme.blue[500]};
	border: none;
	border-radius: 6px;
	transition: background-color 0.15s ease;

	&:hover {
		background-color: ${({ theme }) => theme.blue[400]};
	}
`;
